import { motion, useMotionValue, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShoppingBag } from "lucide-react";
import { MaskedLine, FadeIn } from "@/components/Reveal";
import { useContent } from "@/components/useContent";
import { BRAND } from "@/assets";

export const Hero = () => {
  const navigate = useNavigate();
  const content = useContent();
  const tagline = content.hero_tagline || "Est. 2026 | Independent Record Label & Production.";
  const slogan = content.hero_slogan || "No Venom Needed ~ Just Pure Pressure";

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const logoX = useTransform(mx, [-0.5, 0.5], [-16, 16]);
  const logoY = useTransform(my, [-0.5, 0.5], [-12, 12]);
  const bgX = useTransform(mx, [-0.5, 0.5], [10, -10]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      onMouseMove={onMove}
      data-testid="hero-section"
    >
      <motion.div className="absolute inset-0" style={{ x: bgX }} aria-hidden="true">
        <img
          src={BRAND.banner}
          alt=""
          className="w-full h-full object-cover opacity-45 scale-[1.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/80 via-obsidian/55 to-obsidian" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0A0A0C_78%)]" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 w-full pt-32 pb-24 grid lg:grid-cols-[1.35fr_1fr] gap-12 items-center">
        <div>
          <FadeIn delay={0.15}>
            <div className="inline-flex items-center gap-2 border border-crystal/50 rounded-full px-4 py-1.5 mb-8 bg-crystal/10">
              <span className="w-1.5 h-1.5 rounded-full bg-venom animate-pulse" />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-ice/90">
                Est. 2026 · Official ASCAP® &amp; PRO Members
              </span>
            </div>
          </FadeIn>

          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight" data-testid="hero-headline">
            <MaskedLine delay={0.3}>
              <span className="text-venom drop-shadow-[0_0_22px_rgba(57,255,20,0.35)]">DGE:</span>
            </MaskedLine>
            <MaskedLine delay={0.45}>
              <span className="text-white">COLD-BLOODED</span>
            </MaskedLine>
            <MaskedLine delay={0.6}>
              <span className="text-stroke-crystal">RECORDS LLC™</span>
            </MaskedLine>
          </h1>

          <FadeIn delay={0.85}>
            <p className="mt-7 text-base md:text-lg text-ice/80 max-w-xl leading-relaxed" data-testid="hero-tagline">
              {tagline}
            </p>
            <p className="mt-4 font-mono text-xs sm:text-sm uppercase tracking-[0.3em] text-venom drop-shadow-[0_0_14px_rgba(57,255,20,0.4)]" data-testid="hero-slogan">
              {slogan}
            </p>
          </FadeIn>

          <FadeIn delay={1}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                onClick={() => navigate("/quote")}
                className="group inline-flex items-center gap-2.5 bg-venom text-obsidian font-bold text-sm sm:text-base px-8 py-4 rounded-full hover:glow-venom hover:scale-[1.03] active:scale-95 transition-all duration-300"
                data-testid="hero-cta-quote"
              >
                Request a Quote
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => navigate("/merch")}
                className="inline-flex items-center gap-2.5 border border-crystal/60 text-ice font-semibold text-sm sm:text-base px-8 py-4 rounded-full hover:bg-crystal/15 hover:border-crystal hover:glow-crystal active:scale-95 transition-all duration-300"
                data-testid="hero-cta-merch"
              >
                <ShoppingBag size={17} className="text-venom" />
                Shop Fan Merch
              </button>
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={0.7} className="hidden lg:flex justify-center">
          <motion.div
            style={{ x: logoX, y: logoY }}
            animate={{ y: [0, -14, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="relative"
            data-testid="hero-coin-emblem"
          >
            <div className="absolute inset-0 rounded-full bg-crystal/30 blur-[70px] scale-110" aria-hidden="true" />
            <img
              src={BRAND.coinLogo}
              alt="DGE: Cold-Blooded Records LLC™ serpent coin emblem"
              className="relative w-[340px] xl:w-[400px] aspect-square object-cover rounded-full border border-crystal/40 shadow-[0_0_80px_rgba(138,43,226,0.35)]"
            />
          </motion.div>
        </FadeIn>
      </div>

      <FadeIn delay={1.3} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <div className="flex flex-col items-center gap-2 text-ice/40">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
          <div className="w-px h-10 bg-gradient-to-b from-venom/70 to-transparent" />
        </div>
      </FadeIn>
    </section>
  );
};
